import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, CheckboxGroup, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore } from '../catalog/shared.js';

type Found = Schemas['PrivacySubjectOrders']['orders'];
type LogItem = Schemas['PrivacyRequestList']['items'][number];
type Kind = 'phone' | 'email';

function privacyError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'orders-in-progress') {
      return t('admin.privacy.error.inProgress', { detail: failure.problem.detail ?? '' });
    }
    if (failure.code === 'validation-failed') return t('admin.privacy.error.identifier');
  }
  return errorMessage(t, failure);
}

/** A customer's data requests: find their orders, export the data, erase it. */
export function PrivacyPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime, formatPrice } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [kind, setKind] = useState<Kind>('phone');
  const [value, setValue] = useState('');
  const [subject, setSubject] = useState<{ phone: string } | { email: string } | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const [confirm, setConfirm] = useState<string[]>([]);
  const [log, setLog] = useState<LogItem[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadLog = useCallback(async () => {
    const result = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/privacy/requests', { params: { path: { storeId } } }),
    );
    setLog(result.items);
  }, [storeId]);

  useEffect(() => {
    loadLog().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [loadLog, t]);

  async function search(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    setConfirm([]);
    const next = kind === 'phone' ? { phone: value.trim() } : { email: value.trim() };
    setBusy(true);
    try {
      const result = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/privacy/lookup', {
          params: { path: { storeId } },
          body: next,
        }),
      );
      setSubject(next);
      setFound(result.orders);
    } catch (failure) {
      setError(privacyError(t, failure));
      setFound(null);
    } finally {
      setBusy(false);
    }
  }

  async function exportData() {
    if (subject === null) return;
    setError(null);
    try {
      const document = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/privacy/export', {
          params: { path: { storeId } },
          body: subject,
        }),
      );
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' }),
      );
      const link = globalThis.document.createElement('a');
      link.href = url;
      link.download = 'customer-data.json';
      link.click();
      URL.revokeObjectURL(url);
      setNotice(t('admin.privacy.exported'));
      await loadLog();
    } catch (failure) {
      setError(privacyError(t, failure));
    }
  }

  async function erase() {
    if (subject === null) return;
    setError(null);
    setBusy(true);
    try {
      const result = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/privacy/erase', {
          params: { path: { storeId } },
          body: { ...subject, confirm: true },
        }),
      );
      setNotice(t('admin.privacy.erased', { count: result.ordersErased }));
      setFound([]);
      setConfirm([]);
      await loadLog();
    } catch (failure) {
      setError(privacyError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.privacy.title')}</h1>
      <p>{t('admin.privacy.intro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-card mrigl-stack"
        aria-label={t('admin.privacy.search')}
        onSubmit={(event) => void search(event)}
      >
        <SelectField
          label={t('admin.privacy.identifier')}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as Kind);
          }}
          options={[
            { value: 'phone', label: t('admin.order.phone') },
            { value: 'email', label: t('admin.order.email') },
          ]}
        />
        <TextField
          label={kind === 'phone' ? t('admin.order.phone') : t('admin.order.email')}
          type={kind === 'phone' ? 'tel' : 'email'}
          dir="ltr"
          required
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
        <Button type="submit" busy={busy}>
          {t('admin.privacy.search')}
        </Button>
      </form>

      {found === null ? null : (
        <Card labelledBy="privacy-found">
          <h2 id="privacy-found" className="mrigl-subheading">
            {t('admin.privacy.found', { count: found.length })}
          </h2>
          {found.length === 0 ? null : (
            <>
              <ul className="mrigl-list">
                {found.map((order) => (
                  <li key={order.orderId}>
                    <Link to={`/stores/${storeId}/orders/${order.orderId}`}>
                      {t('admin.order.number', { number: String(order.number) })}
                    </Link>{' '}
                    — {formatDateTime(order.createdAt)} —{' '}
                    {t(`admin.order.status.${order.status as 'placed'}`)} —{' '}
                    {formatPrice(order.total.minor)}
                  </li>
                ))}
              </ul>
              <Button variant="secondary" onClick={() => void exportData()}>
                {t('admin.privacy.export')}
              </Button>
              <p className="mrigl-muted">{t('admin.privacy.eraseHint')}</p>
              <CheckboxGroup
                legend={t('admin.privacy.erase')}
                options={[{ value: 'yes', label: t('admin.privacy.confirm') }]}
                values={confirm}
                onChange={setConfirm}
              />
              <Button busy={busy} disabled={!confirm.includes('yes')} onClick={() => void erase()}>
                {t('admin.privacy.erase')}
              </Button>
            </>
          )}
        </Card>
      )}

      <Card labelledBy="privacy-log">
        <h2 id="privacy-log" className="mrigl-subheading">
          {t('admin.privacy.log')}
        </h2>
        {log.length === 0 ? (
          <p className="mrigl-muted">{t('admin.privacy.noLog')}</p>
        ) : (
          <ul className="mrigl-list">
            {log.map((item) => (
              <li key={item.requestId}>
                {formatDateTime(item.createdAt)} — {t(`admin.privacy.kind.${item.kind}`)} —{' '}
                {t('admin.privacy.ordersAffected', { count: item.ordersAffected })}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
